import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  ListSubheader,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { useStateContext } from "contexts/ContextProvider";
import api from "services/api";
import Overlay from "components/Overlay/Overlay";
// Namespace import: `invalidateValidationCheckConfig` and the new
// `isConditionBackedBuiltIn` both live in the validation engine helper. The
// namespace form keeps this file compiling before/after that export lands —
// a missing accessor simply means "treat the built-in as code-backed".
import * as validationEngine from "helpers/serviceValidationChecks";

/**
 * Settings → Validation Checks: New / Edit overlay.
 *
 * Rendered CONDITIONALLY by the ValidationChecksTab (tab-local state, not the
 * global overlay registry) — Settings mounts this component directly and it
 * receives the row to edit as a prop:
 *   <ValidationCheckNewEdit
 *     check={row}                 // null / omitted → create mode
 *     onClose={() => ...}         // overlay dismissed (back / Discard / ESC)
 *     onSaved={() => ...}         // after a successful save OR delete
 *   />
 *
 * Server contract:
 *   GET    /validationChecks/columns?category=Interpretation|Translation|OtherService
 *          (or ?categories=A,B for a multi-service INTERSECTION)
 *          → [{ name, label, type: 'string'|'number'|'boolean'|'date'|'time', group }]
 *          Every catalog includes the virtual `Status` column for the service.
 *   POST   /validationChecks    → create CUSTOM check (Code auto-generated)
 *   PUT    /validationChecks/:id → full update (metadata + conditions) for
 *          customs AND built-ins; code-backed built-ins only use Applies.
 *   DELETE /validationChecks/:id → 400 for built-ins ("disable instead")
 *
 * Value serialization (engine semantics live in helpers/serviceValidationChecks.js):
 *   in / notIn  → JSON array string, e.g. '["Confirmed","Completed"]'
 *   between     → JSON array string of two numbers, e.g. '[1,10]'
 *   everything else → the raw text value ('true'/'false' for boolean columns)
 *
 * After ANY successful save/delete the module-level check-config cache is
 * invalidated and onSaved() is called so the tab (and, on their next load,
 * the Interpretation lists) re-evaluate.
 */

const SERVICE_CATEGORY_OPTIONS = [
  { value: "Interpretation", label: "Interpretation" },
  { value: "Translation", label: "Translation" },
  { value: "OtherService", label: "Other Service" },
];

// Canonical token order for the CSV ServiceCategory scope shared with the
// server and the Settings tab: "Interpretation,Translation,OtherService".
// Legacy single values are upgraded on load; legacy "All" expands to all
// three; token matching is case-insensitive and treats "OtherService" and
// "Other Service" as the same service.
const CATEGORY_TOKEN_ORDER = ["Interpretation", "Translation", "OtherService"];
const CATEGORY_TOKEN_LABELS = {
  Interpretation: "Interpretation",
  Translation: "Translation",
  OtherService: "Other Service",
};

// CSV → canonical token array (blank → [], unknown tokens dropped).
const parseServiceScope = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return [];
  if (raw.toLowerCase() === "all") return [...CATEGORY_TOKEN_ORDER];
  const seen = new Set();
  raw.split(",").forEach((part) => {
    const norm = part.trim().toLowerCase().replace(/[\s_-]+/g, "");
    if (norm === "interpretation") seen.add("Interpretation");
    else if (norm === "translation") seen.add("Translation");
    else if (norm === "otherservice") seen.add("OtherService");
  });
  return CATEGORY_TOKEN_ORDER.filter((token) => seen.has(token));
};

// Canonical CSV for form state, cache keys, and the save payload.
const serializeServiceScope = (tokens) =>
  CATEGORY_TOKEN_ORDER.filter((token) => tokens.includes(token)).join(",");

// "a translation or other service row" / "an interpretation row" / "any
// service row" — grammar helper for the builder intro copy (multi-selection
// aware).
const describeScopeRow = (tokens) => {
  if (tokens.length === 0) return "a service row";
  if (tokens.length === CATEGORY_TOKEN_ORDER.length) return "any service row";
  const labels = tokens.map((token) =>
    CATEGORY_TOKEN_LABELS[token].toLowerCase(),
  );
  const joined =
    labels.length === 1
      ? labels[0]
      : `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`;
  return `${/^[aeiou]/.test(joined) ? "an" : "a"} ${joined} row`;
};

// Compact scope label for alerts: "all services" / "Translation + Other
// Service" / "Interpretation".
const describeScope = (tokens) => {
  if (tokens.length === 0) return "no service";
  if (tokens.length === CATEGORY_TOKEN_ORDER.length) return "all services";
  return tokens.map((token) => CATEGORY_TOKEN_LABELS[token]).join(" + ");
};

// Condition-backed (converted) built-ins are listed by the validation engine
// helper. Accessed defensively: before that export lands, nothing is
// condition-backed — built-ins render as code-backed (the safe default).
const isConditionBackedBuiltInCheck = (code) => {
  if (!code) return false;
  const isConditionBacked = validationEngine.isConditionBackedBuiltIn;
  return typeof isConditionBacked === "function"
    ? Boolean(isConditionBacked(code))
    : false;
};

const SEVERITY_OPTIONS = [
  { value: "error", label: "Error" },
  { value: "warning", label: "Warning" },
];

// Operator catalog mirroring the engine. `numericOnly` ops hide for
// string/date/boolean columns — on TIME columns they hide too, except the
// field-comparison ops (see getOperatorOptions); `stringOnly` ops hide for
// non-string columns; `field` ops compare against CompareColumn; `tolerance`
// ops show the Tolerance input.
const OPERATORS = [
  { value: "equals", label: "equals", valueKind: "single", tolerance: true },
  { value: "notEquals", label: "does not equal", valueKind: "single", tolerance: true },
  { value: "greaterThan", label: "is greater than", valueKind: "single", numericOnly: true },
  { value: "greaterThanOrEqual", label: "is greater than or equal to", valueKind: "single", numericOnly: true },
  { value: "lessThan", label: "is less than", valueKind: "single", numericOnly: true },
  { value: "lessThanOrEqual", label: "is less than or equal to", valueKind: "single", numericOnly: true },
  { value: "between", label: "is between", valueKind: "between", numericOnly: true },
  { value: "in", label: "is one of", valueKind: "list" },
  { value: "notIn", label: "is not one of", valueKind: "list" },
  { value: "contains", label: "contains", valueKind: "single", stringOnly: true },
  { value: "notContains", label: "does not contain", valueKind: "single", stringOnly: true },
  { value: "startsWith", label: "starts with", valueKind: "single", stringOnly: true },
  { value: "endsWith", label: "ends with", valueKind: "single", stringOnly: true },
  { value: "isBlank", label: "is blank", valueKind: "none" },
  { value: "isNotBlank", label: "is not blank", valueKind: "none" },
  { value: "isZero", label: "is zero", valueKind: "none", numericOnly: true },
  { value: "isNotZero", label: "is not zero", valueKind: "none", numericOnly: true },
  { value: "equalsField", label: "equals column", valueKind: "none", field: true },
  { value: "notEqualsField", label: "does not equal column", valueKind: "none", field: true },
  { value: "greaterThanField", label: "is greater than column", valueKind: "none", field: true, numericOnly: true },
  { value: "lessThanField", label: "is less than column", valueKind: "none", field: true, numericOnly: true },
  { value: "equalsFieldTolerance", label: "equals column (within tolerance)", valueKind: "none", field: true, tolerance: true, numericOnly: true },
];

const OPERATOR_META = (() => {
  const map = {};
  for (const operator of OPERATORS) map[operator.value] = operator;
  return map;
})();

const KNOWN_COLUMN_TYPES = ["string", "number", "boolean", "date", "time"];

// Operators for one column type (unknown type → the full list).
//
// TIME exception: field-comparison ops keep their `numericOnly` flag for
// number columns, but on TIME columns we still offer the ones the engine
// evaluates as times of day — lessThanField / greaterThanField (time-aware
// since 2026-10-12) plus equalsField / notEqualsField, which already pass
// this filter because they carry no `numericOnly` flag. equalsFieldTolerance
// stays number-only: its tolerance window is a numeric amount with no
// defined time unit. The seeded converted checks (time_order,
// actual_time_order) rely on these operators being re-pickable.
const getOperatorOptions = (columnType) => {
  if (!KNOWN_COLUMN_TYPES.includes(columnType)) return OPERATORS;
  return OPERATORS.filter((operator) => {
    if (operator.numericOnly && columnType !== "number") {
      return columnType === "time" && operator.field && !operator.tolerance;
    }
    if (operator.stringOnly && columnType !== "string") return false;
    return true;
  });
};

const NO_VALUE_OPERATORS = ["isBlank", "isNotBlank", "isZero", "isNotZero"];
const LIST_OPERATORS = ["in", "notIn"];
const FIELD_OPERATORS = [
  "equalsField",
  "notEqualsField",
  "greaterThanField",
  "lessThanField",
  "equalsFieldTolerance",
];
const TOLERANCE_OPERATORS = ["equals", "notEquals", "equalsFieldTolerance"];

const showsValueInput = (operator) =>
  !NO_VALUE_OPERATORS.includes(operator) && !FIELD_OPERATORS.includes(operator);
const showsCompareColumn = (operator) => FIELD_OPERATORS.includes(operator);
const showsTolerance = (operator) => TOLERANCE_OPERATORS.includes(operator);

// Sequelize TINYINT/BOOLEAN can arrive as true/1/"1"/"true".
const isTruthyFlag = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const parseConditionsPayload = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string" && raw.trim() !== "") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

// Stored Value → editable draft: JSON array strings (in/notIn/between) show
// as comma-separated text; everything else passes through as-is.
const storedValueToDraft = (operator, value) => {
  if (value === null || value === undefined) return "";
  const text = String(value);
  if (LIST_OPERATORS.includes(operator) || operator === "between") {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item)).join(", ");
      }
    } catch {
      return text;
    }
  }
  return text;
};

const splitListText = (value) =>
  String(value ?? "")
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part !== "");

let conditionRowCounter = 0;
const createConditionRow = (overrides = {}) => {
  conditionRowCounter += 1;
  return {
    rowKey: `condition-${conditionRowCounter}`,
    JoinOperator: "AND",
    ColumnName: "",
    Operator: "equals",
    Value: "",
    CompareColumn: "",
    Tolerance: "",
    ...overrides,
  };
};

const buildConditionRows = (check, clause) =>
  parseConditionsPayload(check?.Conditions)
    .filter(
      (condition) =>
        String(condition?.Clause || "").trim().toLowerCase() ===
        clause.toLowerCase(),
    )
    .sort((a, b) => (Number(a?.Seq) || 0) - (Number(b?.Seq) || 0))
    .map((condition) => {
      const operator = String(condition?.Operator || "").trim() || "equals";
      return createConditionRow({
        JoinOperator:
          String(condition?.JoinOperator || "").trim().toUpperCase() === "OR"
            ? "OR"
            : "AND",
        ColumnName: String(condition?.ColumnName || "").trim(),
        Operator: operator,
        Value: storedValueToDraft(operator, condition?.Value),
        CompareColumn: condition?.CompareColumn
          ? String(condition.CompareColumn).trim()
          : "",
        Tolerance:
          condition?.Tolerance === null ||
          condition?.Tolerance === undefined ||
          condition?.Tolerance === ""
            ? ""
            : String(condition.Tolerance),
      });
    });

const buildInitialForm = (check) => ({
  Name: String(check?.Name || ""),
  Description: String(check?.Description || ""),
  // Canonical CSV scope — legacy single values and "All" are normalized on
  // load so the checkbox state, catalog cache keys, and save payload always
  // agree with the server's storage format.
  ServiceCategory: serializeServiceScope(
    parseServiceScope(check ? check.ServiceCategory : "Interpretation"),
  ),
  Severity: check?.Severity === "warning" ? "warning" : "error",
  IsEnabled: check ? isTruthyFlag(check.IsEnabled) : true,
  SortOrder:
    check?.SortOrder === null || check?.SortOrder === undefined
      ? ""
      : String(check.SortOrder),
  AppliesLogic:
    String(check?.AppliesLogic || "").trim().toLowerCase() === "any"
      ? "Any"
      : "All",
  FailsLogic:
    String(check?.FailsLogic || "").trim().toLowerCase() === "any"
      ? "Any"
      : "All",
  appliesRows: buildConditionRows(check, "Applies"),
  failsRows: buildConditionRows(check, "Fails"),
});

const normalizeCatalogColumn = (column) => {
  const name = String(column?.name ?? column?.Name ?? "").trim();
  return {
    name,
    label: String(column?.label ?? column?.Label ?? name).trim() || name,
    type: String(column?.type ?? column?.Type ?? "").trim().toLowerCase(),
    group: String(column?.group ?? column?.Group ?? "Other").trim() || "Other",
  };
};

const ValidationCheckNewEdit = ({ check = null, onClose, onSaved }) => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { showSnackbar, handleErrorDialog, openDialog, closeDialog } =
    useStateContext();

  const isEditMode = check !== null && check !== undefined;
  const isBuiltIn = isEditMode && isTruthyFlag(check.IsBuiltIn);
  // CODE-BACKED built-ins are computed by the engine formula: the overlay
  // shows their Applies section as an extra gate and hides Fails (the engine
  // ignores it for them). Converted (condition-backed) built-ins get the
  // full builder — their seeded conditions load like a custom check's.
  const codeBackedBuiltIn =
    isBuiltIn && !isConditionBackedBuiltInCheck(check?.Code);

  const [form, setForm] = useState(() => buildInitialForm(check));
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // ── Column catalog (per selected category) ──────────────────────────
  const [columnCatalog, setColumnCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(false);
  const catalogCacheRef = useRef({});
  const catalogRequestRef = useRef(0);

  const loadColumns = useCallback(async (selection) => {
    // Cache + request keys use the canonical CSV scope, so single and
    // multi-service selections never collide in the cache and toggling back
    // to a previous combination hits the cache. Multi-selection asks the
    // server for the column INTERSECTION via ?categories=; a single
    // selection keeps the ?category= form.
    const tokens = parseServiceScope(selection);
    if (tokens.length === 0) {
      // Nothing selected — clear the builder until a service is picked.
      catalogRequestRef.current += 1; // invalidate any in-flight response
      setColumnCatalog([]);
      setCatalogError(false);
      setCatalogLoading(false);
      return;
    }
    const cacheKey = serializeServiceScope(tokens);

    const cached = catalogCacheRef.current[cacheKey];
    if (cached) {
      setColumnCatalog(cached);
      setCatalogError(false);
      setCatalogLoading(false);
      return;
    }

    const requestId = catalogRequestRef.current + 1;
    catalogRequestRef.current = requestId;
    setCatalogLoading(true);
    setCatalogError(false);
    try {
      const response = await api.get("/validationChecks/columns", {
        params:
          tokens.length > 1
            ? { categories: cacheKey }
            : { category: tokens[0] },
      });
      if (catalogRequestRef.current !== requestId) return; // stale response
      const list = Array.isArray(response?.data) ? response.data : [];
      const normalized = list
        .map(normalizeCatalogColumn)
        .filter((column) => column.name);
      catalogCacheRef.current[cacheKey] = normalized;
      setColumnCatalog(normalized);
      setCatalogError(false);
    } catch (error) {
      if (catalogRequestRef.current !== requestId) return;
      console.error("Error fetching validation check columns:", error);
      setColumnCatalog([]);
      setCatalogError(true);
    } finally {
      if (catalogRequestRef.current === requestId) {
        setCatalogLoading(false);
      }
    }
  }, []);

  // Load the column catalog for the CURRENT selection — custom checks AND
  // built-ins (built-ins are fully visible/editable in this overlay now).
  useEffect(() => {
    void loadColumns(form.ServiceCategory);
  }, [form.ServiceCategory, loadColumns]);

  const columnsByName = useMemo(() => {
    const map = new Map();
    for (const column of columnCatalog) map.set(column.name, column);
    return map;
  }, [columnCatalog]);

  const groupedColumns = useMemo(() => {
    const groups = new Map();
    for (const column of columnCatalog) {
      if (!groups.has(column.group)) groups.set(column.group, []);
      groups.get(column.group).push(column);
    }
    return Array.from(groups.entries());
  }, [columnCatalog]);

  const catalogReady = !catalogLoading && !catalogError && columnCatalog.length > 0;

  // ── Form updates ────────────────────────────────────────────────────
  const setField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  // Multi-select category toggle — form state stays the canonical CSV so the
  // catalog effect, cache keys, and save payload all derive from one source.
  // Unchecking the last service is allowed here; save is blocked by the
  // inline error + buildSavePayload guard until one is picked again.
  const handleCategoryToggle = (token) => {
    const current = parseServiceScope(form.ServiceCategory);
    const next = current.includes(token)
      ? current.filter((item) => item !== token)
      : [...current, token];
    setField("ServiceCategory", serializeServiceScope(next));
  };

  const updateConditionRow = (clauseKey, rowKey, patch) =>
    setForm((prev) => ({
      ...prev,
      [clauseKey]: prev[clauseKey].map((row) =>
        row.rowKey === rowKey ? { ...row, ...patch } : row,
      ),
    }));

  const handleAddCondition = (clauseKey) =>
    setForm((prev) => ({
      ...prev,
      [clauseKey]: [...prev[clauseKey], createConditionRow()],
    }));

  const handleRemoveCondition = (clauseKey, rowKey) =>
    setForm((prev) => ({
      ...prev,
      [clauseKey]: prev[clauseKey].filter((row) => row.rowKey !== rowKey),
    }));

  // Changing the column may invalidate the current operator (e.g. a
  // numeric-only op on a string column) — reset to an allowed default.
  const handleColumnChange = (clauseKey, row, nextColumnName) => {
    const nextType = columnsByName.get(nextColumnName)?.type;
    const allowed = getOperatorOptions(nextType);
    const patch = { ColumnName: nextColumnName };
    if (!allowed.some((operator) => operator.value === row.Operator)) {
      patch.Operator = "equals";
      patch.Value = "";
      patch.CompareColumn = "";
      patch.Tolerance = "";
    }
    updateConditionRow(clauseKey, row.rowKey, patch);
  };

  // Section logic is the bulk control: All → every row joins with AND,
  // Any → OR. A single row's Join select still overrides just that row.
  const handleLogicChange = (clauseKey, logicKey, nextLogic) =>
    setForm((prev) => ({
      ...prev,
      [logicKey]: nextLogic,
      [clauseKey]: prev[clauseKey].map((row) => ({
        ...row,
        JoinOperator: nextLogic === "Any" ? "OR" : "AND",
      })),
    }));

  // ── Validation + payload ────────────────────────────────────────────
  const validateConditionRows = (rows, clauseLabel) => {
    for (let index = 0; index < rows.length; index += 1) {
      const row = rows[index];
      const where = `${clauseLabel} condition ${index + 1}`;

      if (!row.ColumnName) return `Pick a column for ${where}.`;
      if (catalogReady && !columnsByName.has(row.ColumnName)) {
        return `Pick a valid column for ${where} — "${row.ColumnName}" isn't in the current column list.`;
      }
      if (!OPERATOR_META[row.Operator]) {
        return `Pick an operator for ${where}.`;
      }
      if (showsCompareColumn(row.Operator) && !row.CompareColumn) {
        return `Pick a compare column for ${where}.`;
      }

      const toleranceRaw = String(row.Tolerance ?? "").trim();
      if (showsTolerance(row.Operator) && toleranceRaw !== "") {
        const toleranceValue = Number(toleranceRaw);
        if (!Number.isFinite(toleranceValue) || toleranceValue < 0) {
          return `Tolerance for ${where} must be a number of 0 or more.`;
        }
      }

      if (!showsValueInput(row.Operator)) continue;

      if (row.Operator === "between") {
        const parts = splitListText(row.Value);
        const bothNumeric =
          parts.length === 2 &&
          parts.every((part) => part !== "" && Number.isFinite(Number(part)));
        if (!bothNumeric) {
          return `"Between" for ${where} needs two numbers (e.g. "1, 10").`;
        }
        continue;
      }
      if (LIST_OPERATORS.includes(row.Operator)) {
        if (splitListText(row.Value).length === 0) {
          return `Enter at least one value for ${where}.`;
        }
        continue;
      }
      if (String(row.Value ?? "").trim() === "") {
        return `Enter a value for ${where}.`;
      }
    }
    return null;
  };

  const serializeConditionValue = (row, columnType) => {
    if (!showsValueInput(row.Operator)) return null;
    const raw = String(row.Value ?? "").trim();

    if (row.Operator === "between") {
      const parts = splitListText(raw);
      return JSON.stringify([Number(parts[0]), Number(parts[1])]);
    }
    if (LIST_OPERATORS.includes(row.Operator)) {
      const parts = splitListText(raw);
      const allNumeric =
        parts.length > 0 && parts.every((part) => Number.isFinite(Number(part)));
      const serialized =
        columnType === "number" && allNumeric
          ? parts.map((part) => Number(part))
          : parts;
      return JSON.stringify(serialized);
    }
    if (
      columnType === "boolean" &&
      (row.Operator === "equals" || row.Operator === "notEquals")
    ) {
      return row.Value === "false" ? "false" : "true";
    }
    return raw;
  };

  const buildConditionsPayload = () => {
    const toPayloadRow = (row, clause, seq) => {
      const columnType = columnsByName.get(row.ColumnName)?.type || null;
      const toleranceRaw = String(row.Tolerance ?? "").trim();
      return {
        Clause: clause,
        Seq: seq,
        JoinOperator: row.JoinOperator === "OR" ? "OR" : "AND",
        ColumnName: row.ColumnName,
        Operator: row.Operator,
        Value: serializeConditionValue(row, columnType),
        CompareColumn: showsCompareColumn(row.Operator)
          ? row.CompareColumn || null
          : null,
        Tolerance:
          showsTolerance(row.Operator) && toleranceRaw !== ""
            ? Number(toleranceRaw)
            : null,
      };
    };

    return [
      ...form.appliesRows.map((row, index) =>
        toPayloadRow(row, "Applies", index + 1),
      ),
      ...form.failsRows.map((row, index) =>
        toPayloadRow(row, "Fails", index + 1),
      ),
    ];
  };

  const buildSavePayload = () => {
    const name = form.Name.trim();
    if (!name) {
      showSnackbar("Check name is required", theme, "error");
      return null;
    }

    const scopeTokens = parseServiceScope(form.ServiceCategory);
    if (scopeTokens.length === 0) {
      showSnackbar("Pick at least one service category", theme, "error");
      return null;
    }

    let sortOrder = null;
    const sortOrderRaw = String(form.SortOrder ?? "").trim();
    if (sortOrderRaw !== "") {
      sortOrder = Number(sortOrderRaw);
      if (!Number.isFinite(sortOrder)) {
        showSnackbar("Sort order must be a number", theme, "error");
        return null;
      }
    }

    // CODE-BACKED built-ins show the Applies section as an extra gate and no
    // Fails section — the "≥1 Fails condition" rule only applies to custom
    // checks and condition-backed (converted) built-ins.
    if (!codeBackedBuiltIn && form.failsRows.length === 0) {
      showSnackbar(
        'Add at least one condition under "Fails When" — a check needs a condition that makes it fail.',
        theme,
        "error",
      );
      return null;
    }
    const appliesProblem = validateConditionRows(form.appliesRows, '"Applies When"');
    if (appliesProblem) {
      showSnackbar(appliesProblem, theme, "error");
      return null;
    }
    if (!codeBackedBuiltIn) {
      const failsProblem = validateConditionRows(form.failsRows, '"Fails When"');
      if (failsProblem) {
        showSnackbar(failsProblem, theme, "error");
        return null;
      }
    }

    // Built-ins save through the SAME full payload as customs — the server
    // relaxes their update path in parallel; the engine decides what it
    // evaluates, not the overlay.
    const payload = {
      Name: name,
      Description: form.Description.trim(),
      ServiceCategory: serializeServiceScope(scopeTokens),
      Severity: form.Severity,
      IsEnabled: form.IsEnabled,
      AppliesLogic: form.AppliesLogic,
      FailsLogic: form.FailsLogic,
      Conditions: buildConditionsPayload(),
    };
    if (sortOrder !== null) payload.SortOrder = sortOrder;
    return payload;
  };

  // ── Save / delete ───────────────────────────────────────────────────
  const notifyConfigChanged = () => {
    try {
      if (typeof validationEngine.invalidateValidationCheckConfig === "function") {
        validationEngine.invalidateValidationCheckConfig();
      }
    } catch (error) {
      console.error("Error invalidating validation check config:", error);
    }
  };

  const handleSave = async () => {
    if (saving || deleting) return;
    const payload = buildSavePayload();
    if (!payload) return;

    setSaving(true);
    try {
      if (isEditMode) {
        await api.put(`/validationChecks/${check.id}`, payload);
      } else {
        await api.post("/validationChecks", payload);
      }
      showSnackbar(isEditMode ? "Check updated" : "Check created", theme, "success");
      notifyConfigChanged();
      onSaved?.();
      onClose?.();
    } catch (error) {
      console.error("Error saving validation check:", error);
      handleErrorDialog(
        error?.response?.data?.error || "Error saving validation check",
        theme,
      );
    } finally {
      setSaving(false);
    }
  };

  const performDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/validationChecks/${check.id}`);
      showSnackbar("Check deleted", theme, "success");
      notifyConfigChanged();
      onSaved?.();
      closeDialog();
      onClose?.();
    } catch (error) {
      console.error("Error deleting validation check:", error);
      closeDialog();
      handleErrorDialog(
        error?.response?.data?.error || "Error deleting validation check",
        theme,
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteClick = () => {
    if (!isEditMode || isBuiltIn) return;
    openDialog({
      theme,
      title: "Delete Validation Check",
      message: `Delete "${form.Name.trim() || check.Name}"? This cannot be undone.`,
      primaryAction: "Delete",
      secondaryAction: "Cancel",
      primaryOnClick: () => {
        void performDelete();
      },
      secondaryOnClick: closeDialog,
    });
  };

  // ── Render helpers ──────────────────────────────────────────────────
  // NOTE: these helpers must return FLAT ARRAYS of select children, not
  // fragments. MUI v6 SelectInput runs `React.Children.toArray(children)`
  // which does NOT flatten fragments → "MUI: The Select component doesn't
  // accept a Fragment as a child" and a blank select when the persisted
  // value can't be resolved (breaking edit mode for existing checks).
  const renderCatalogOptions = (selectedValue) => {
    const options = groupedColumns.flatMap(([group, columns]) => [
      <ListSubheader key={`group-${group}`} sx={{ fontWeight: 700 }}>
        {group}
      </ListSubheader>,
      ...columns.map((column) => (
        <MenuItem key={column.name} value={column.name}>
          {column.label}
        </MenuItem>
      )),
    ]);
    // Preserve a persisted value that is not in the catalog (column removed
    // server-side) so the select can still resolve and display it.
    if (selectedValue && !columnsByName.has(selectedValue)) {
      options.push(
        <MenuItem key={selectedValue} value={selectedValue}>
          {selectedValue}
        </MenuItem>,
      );
    }
    return options;
  };

  const renderOperatorOptions = (row) => {
    const columnType = columnsByName.get(row.ColumnName)?.type;
    const options = getOperatorOptions(columnType).map((operator) => (
      <MenuItem key={operator.value} value={operator.value}>
        {operator.label}
      </MenuItem>
    ));
    // Preserve a stored operator that is no longer in OPERATOR_META
    // (legacy rows) so the select can still resolve and display it.
    if (!OPERATOR_META[row.Operator] && row.Operator) {
      options.push(
        <MenuItem key={row.Operator} value={row.Operator}>
          {row.Operator}
        </MenuItem>,
      );
    }
    return options;
  };

  const renderValueInput = (clauseKey, row) => {
    const columnType = columnsByName.get(row.ColumnName)?.type;

    if (
      columnType === "boolean" &&
      (row.Operator === "equals" || row.Operator === "notEquals")
    ) {
      return (
        <TextField
          select
          size="small"
          label="Value"
          value={row.Value === "false" ? "false" : "true"}
          onChange={(event) =>
            updateConditionRow(clauseKey, row.rowKey, {
              Value: event.target.value,
            })
          }
          sx={{ flex: "1 1 150px", minWidth: 130 }}
        >
          <MenuItem value="true">true</MenuItem>
          <MenuItem value="false">false</MenuItem>
        </TextField>
      );
    }

    const isBetween = row.Operator === "between";
    const isList = LIST_OPERATORS.includes(row.Operator);
    return (
      <TextField
        size="small"
        label={isBetween ? "Min, Max" : isList ? "Values (comma separated)" : "Value"}
        placeholder={isBetween ? "e.g. 1, 10" : isList ? "e.g. Confirmed, Completed" : ""}
        value={row.Value}
        onChange={(event) =>
          updateConditionRow(clauseKey, row.rowKey, {
            Value: event.target.value,
          })
        }
        sx={{ flex: "1 1 150px", minWidth: 130 }}
      />
    );
  };

  const renderConditionRow = (clauseKey, row, index) => (
    <Box
      key={row.rowKey}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap",
        mb: 1,
      }}
    >
      {index > 0 ? (
        <TextField
          select
          size="small"
          value={row.JoinOperator}
          onChange={(event) =>
            updateConditionRow(clauseKey, row.rowKey, {
              JoinOperator: event.target.value,
            })
          }
          inputProps={{ "aria-label": `Join operator for condition ${index + 1}` }}
          sx={{ width: 92, "& .MuiInputBase-root": { fontWeight: 600 } }}
        >
          <MenuItem value="AND">AND</MenuItem>
          <MenuItem value="OR">OR</MenuItem>
        </TextField>
      ) : null}

      <TextField
        select
        size="small"
        label="Column"
        value={row.ColumnName}
        onChange={(event) =>
          handleColumnChange(clauseKey, row, event.target.value)
        }
        slotProps={{ select: { displayEmpty: true } }}
        sx={{ flex: "1 1 180px", minWidth: 160 }}
      >
        {renderCatalogOptions(row.ColumnName)}
      </TextField>

      <TextField
        select
        size="small"
        label="Operator"
        value={row.Operator}
        onChange={(event) =>
          updateConditionRow(clauseKey, row.rowKey, {
            Operator: event.target.value,
          })
        }
        sx={{ flex: "1 1 170px", minWidth: 150 }}
      >
        {renderOperatorOptions(row)}
      </TextField>

      {showsValueInput(row.Operator) ? renderValueInput(clauseKey, row) : null}

      {showsCompareColumn(row.Operator) ? (
        <TextField
          select
          size="small"
          label="Compare With"
          value={row.CompareColumn}
          onChange={(event) =>
            updateConditionRow(clauseKey, row.rowKey, {
              CompareColumn: event.target.value,
            })
          }
          slotProps={{ select: { displayEmpty: true } }}
          sx={{ flex: "1 1 170px", minWidth: 150 }}
        >
          {renderCatalogOptions(row.CompareColumn)}
        </TextField>
      ) : null}

      {showsTolerance(row.Operator) ? (
        <TextField
          size="small"
          label="Tolerance"
          type="number"
          value={row.Tolerance}
          onChange={(event) =>
            updateConditionRow(clauseKey, row.rowKey, {
              Tolerance: event.target.value,
            })
          }
          inputProps={{ min: 0, step: "0.01" }}
          sx={{ width: 120 }}
        />
      ) : null}

      <Tooltip title="Remove condition" placement="top">
        <IconButton
          size="small"
          color="error"
          onClick={() => handleRemoveCondition(clauseKey, row.rowKey)}
          aria-label={`Remove ${clauseKey === "appliesRows" ? "Applies" : "Fails"} condition ${index + 1}`}
          sx={{ flexShrink: 0 }}
        >
          <DeleteOutlineRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const renderClauseSection = (clauseKey, logicKey, title) => {
    const rows = form[clauseKey];
    return (
      <Box
        sx={{
          border: `1px solid ${theme.palette.primary.border2}`,
          borderRadius: "8px",
          p: 1.5,
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            mb: rows.length > 0 ? 1.25 : 0.5,
          }}
        >
          <Typography variant="body1" fontWeight={600} sx={{ mr: "auto" }}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Match
          </Typography>
          <TextField
            select
            size="small"
            value={form[logicKey]}
            onChange={(event) =>
              handleLogicChange(clauseKey, logicKey, event.target.value)
            }
            inputProps={{ "aria-label": `${title} logic` }}
            sx={{ width: 92, "& .MuiInputBase-root": { fontWeight: 600 } }}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="Any">Any</MenuItem>
          </TextField>
          <Typography variant="caption" color="text.secondary">
            of these conditions
          </Typography>
        </Box>

        {clauseKey === "appliesRows" && rows.length === 0 ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5 }}
          >
            No conditions — this check applies to every row.
          </Typography>
        ) : null}

        {rows.map((row, index) => renderConditionRow(clauseKey, row, index))}

        <Button
          size="small"
          startIcon={<AddRoundedIcon />}
          onClick={() => handleAddCondition(clauseKey)}
          disabled={!catalogReady}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Add Condition
        </Button>
      </Box>
    );
  };

  // ── Footer ──────────────────────────────────────────────────────────
  const pageTitle = isEditMode ? "Edit Validation Check" : "New Validation Check";

  const footerActions = (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        alignItems: "center",
        width: "100%",
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {isEditMode && !isBuiltIn && (
          isMobile ? (
            <Tooltip title="Delete check" placement="top">
              <IconButton
                color="error"
                onClick={handleDeleteClick}
                disabled={saving || deleting}
                sx={{
                  width: "40px",
                  height: "40px",
                  border: `2px solid ${theme.palette.error.main}`,
                  borderRadius: "12px",
                }}
              >
                <DeleteRoundedIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              color="error"
              variant="outlined"
              size="large"
              endIcon={<DeleteRoundedIcon />}
              onClick={handleDeleteClick}
              disabled={saving || deleting}
              sx={{ borderRadius: "12px" }}
            >
              Delete
            </Button>
          )
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {isMobile ? (
          <Tooltip title="Discard" placement="top">
            <IconButton
              color="primary"
              onClick={onClose}
              disabled={saving || deleting}
              sx={{
                width: "40px",
                height: "40px",
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: "12px",
              }}
            >
              <DisabledByDefaultRoundedIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            color="primary"
            variant="outlined"
            size="large"
            onClick={onClose}
            disabled={saving || deleting}
            sx={{ borderRadius: "12px" }}
          >
            Discard
          </Button>
        )}

        {isMobile ? (
          <IconButton
            color="primary"
            onClick={handleSave}
            disabled={saving || deleting}
            aria-label="Save check"
            sx={{
              width: "40px",
              height: "40px",
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.background.default,
              borderRadius: "12px",
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
              "&.Mui-disabled": {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
            }}
          >
            <SaveRoundedIcon />
          </IconButton>
        ) : (
          <Button
            loading={saving}
            color="primary"
            variant="contained"
            size="large"
            endIcon={<SaveRoundedIcon />}
            onClick={handleSave}
            disabled={saving || deleting}
            sx={{ borderRadius: "12px" }}
          >
            Save
          </Button>
        )}
      </Box>
    </Box>
  );

  const selectedScope = parseServiceScope(form.ServiceCategory);
  const scopeSummary = describeScope(selectedScope);

  return (
    <Overlay
      id="validation-check-new-edit"
      open={true}
      onClose={onClose}
      title={pageTitle}
      subtitle={isEditMode && check?.Code ? `Code: ${check.Code}` : undefined}
      height="fit-content"
      footer={footerActions}
    >
      <Box
        sx={{
          flexGrow: 1,
          p: "1rem 2.3rem 1rem 2.5rem",
          height: "100%",
          width: "100%",
          overflowY: "auto",
          "& .MuiTextField-root": {
            m: 0,
          },
        }}
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Check Details
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Name"
              value={form.Name}
              onChange={(event) => setField("Name", event.target.value)}
              inputProps={{ maxLength: 120 }}
              placeholder="e.g., Client charge below linguist pay"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              sx={{
                border: `1px solid ${theme.palette.primary.border2}`,
                borderRadius: "8px",
                px: 1.5,
                pt: 0.75,
                pb: selectedScope.length === 0 ? 0.25 : 0.75,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                Applies To — select one, two, or all three services
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  columnGap: 1.5,
                }}
              >
                {SERVICE_CATEGORY_OPTIONS.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedScope.includes(option.value)}
                        onChange={() => handleCategoryToggle(option.value)}
                        disabled={saving || deleting}
                        inputProps={{ "aria-label": option.label }}
                      />
                    }
                    label={
                      <Typography variant="body2">{option.label}</Typography>
                    }
                    sx={{ mr: 0 }}
                  />
                ))}
              </Box>
              {selectedScope.length === 0 ? (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ display: "block", mt: 0.25 }}
                >
                  Pick at least one service.
                </Typography>
              ) : null}
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Severity"
              value={form.Severity}
              onChange={(event) => setField("Severity", event.target.value)}
              sx={{
                "& .MuiInputBase-root": {
                  fontWeight: 600,
                  color:
                    form.Severity === "error"
                      ? theme.palette.error.main
                      : theme.palette.warning.main,
                },
              }}
            >
              {SEVERITY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              label="Description"
              value={form.Description}
              onChange={(event) => setField("Description", event.target.value)}
              inputProps={{ maxLength: 300 }}
              placeholder="Shown as the issue message when the check fails"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Sort Order"
              type="number"
              value={form.SortOrder}
              onChange={(event) => setField("SortOrder", event.target.value)}
              inputProps={{ step: 1 }}
              placeholder="Optional"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.IsEnabled}
                  onChange={(event) => setField("IsEnabled", event.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body1">
                  {form.IsEnabled
                    ? "Enabled (the check runs on every evaluation)"
                    : "Disabled (the check is skipped)"}
                </Typography>
              }
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Conditions
            </Typography>

            {codeBackedBuiltIn ? (
              <Box
                sx={{
                  p: 1.5,
                  mb: 1.5,
                  borderRadius: "8px",
                  border: `1px solid ${theme.palette.primary.border2}`,
                  bgcolor: theme.palette.action.hover,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  This check is computed by the Portal (formula-based). You can
                  edit its metadata and add conditions to control WHEN it
                  applies; the core calculation itself isn't condition-based.
                </Typography>
              </Box>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                The check fails {describeScopeRow(selectedScope)} when the{" "}
                <strong>Applies When</strong> conditions match and the{" "}
                <strong>Fails When</strong> conditions also match.
              </Typography>
            )}

            {catalogLoading ? (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
              >
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Loading columns…
                </Typography>
              </Box>
            ) : null}

            {catalogError ? (
              <Alert
                severity="warning"
                sx={{ mb: 1.5, borderRadius: "8px" }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => loadColumns(form.ServiceCategory)}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                  >
                    Retry
                  </Button>
                }
              >
                Couldn't load the column list for {scopeSummary}. Conditions
                can't be edited until it loads.
              </Alert>
            ) : null}

            {!catalogLoading &&
            !catalogError &&
            columnCatalog.length === 0 &&
            selectedScope.length > 0 ? (
              <Alert severity="info" sx={{ mb: 1.5, borderRadius: "8px" }}>
                No columns are available for {scopeSummary} yet.
              </Alert>
            ) : null}

            {renderClauseSection("appliesRows", "AppliesLogic", "Applies When")}
            {codeBackedBuiltIn
              ? null
              : renderClauseSection("failsRows", "FailsLogic", "Fails When")}
          </Grid>
        </Grid>
      </Box>
    </Overlay>
  );
};

export default ValidationCheckNewEdit;
